import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { BarChart2, Bell, Database, Download, FileSearch, MessageSquare, RefreshCw, Settings } from 'lucide-react';

import { PageHeader, TabsTruot } from '../../../components/shared';
import { DropdownMenu } from '../../../components/ui';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { luuBlob } from '../../../lib/api/http';
import { ngayGioVN } from '../../../lib/dinh-dang';
import { xuatExcel, xuatPdf } from '../../../lib/repositories/bao-cao.repository';
import { useAuthStore } from '../../../stores/auth';
import { useTenantCuaToi } from '../../auth/hooks/useTenantCuaToi';
import { KHOA_BAN_TOKEN } from '../../goi-nap-token/hooks/useGoiNapToken';
import { KHOA_TOKEN_AI } from '../../tenant/hooks/useHanMucTokenAi';
import { KHOA_MAN_TOKEN_AI, useLaQuanTriToChuc } from '../../token-ai/hooks/useTokenAi';
import { THAM_SO_MUC_TOKEN } from '../../token-ai/nghiep-vu/token-ai';
import { Panel, DanhSachThanh } from '../components/Panel';
import { TabTokenAi } from '../components/TabTokenAi';
import { useBaoCao } from '../hooks/useBaoCao';
import { tongHop } from '../nghiep-vu/tong-hop';

export function BaoCaoView() {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const navigate = useDieuHuong();
  const user = useAuthStore((s) => s.user);
  const q = useBaoCao();
  const qc = useQueryClient();
  const [thamSo, setThamSo] = useSearchParams();
  const laQuanTri = useLaQuanTriToChuc();
  const { tenantId } = useTenantCuaToi();
  // Token AI chỉ dành cho quản trị tổ chức: người khác không thấy thẻ, không hiện mờ.
  const coTokenAi = laQuanTri && Boolean(tenantId);

  const loading = q.isLoading;
  const tong = useMemo(
    () => (q.data ? tongHop(q.data, (khoa) => t(khoa as never), t('baoCao.chuaPhanCong')) : null),
    [q.data, t],
  );

  const monthChip = new Date().toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  const avatarLetter = user?.fullName?.trim()?.[0]?.toUpperCase() ?? 'U';
  const props = { loading, locale };

  const xuat = useCallback(
    async (dinhDang: 'xlsx' | 'pdf') => {
      if (!tong) return;
      const ngay = t('baoCao.xuatNgay', { ngay: ngayGioVN(new Date().toISOString()) });
      const tieuDe = t('baoCao.xuatTieuDe');
      const tenTep = `Bao-cao-thong-ke-${new Date().toISOString().slice(0, 10)}`;
      const tx = (khoa: string) => t(khoa as never);
      if (dinhDang === 'xlsx') {
        luuBlob(xuatExcel(tong, tieuDe, ngay, tx), `${tenTep}.xlsx`);
      } else {
        luuBlob(await xuatPdf(tong, tieuDe, ngay, tx), `${tenTep}.pdf`);
      }
    },
    [tong, t],
  );

  const the = thamSo.get('the');
  const moSan = the === 'token-ai' && coTokenAi ? 'token-ai' : 'tong-quan';

  const tongQuan = (
    <div className="space-y-4">
    {q.error && (
      <div role="alert" className="flex items-center justify-between gap-3 rounded-lg bg-danger-50 px-4 py-3">
        <span className="text-sm text-danger-700">{t('baoCao.loiTatCa', { loi: dichLoi(q.error) })}</span>
        <button
          type="button"
          onClick={() => void q.refetch()}
          className="shrink-0 rounded-lg border border-default bg-surface px-3 py-1.5 text-xs font-medium text-content-secondary transition-colors hover:bg-subtle"
        >
          {t('chung.thuLai')}
        </button>
      </div>
    )}
    {tong && tong.soLoi > 0 && (
      <div role="status" className="rounded-lg bg-warning-50 px-4 py-3 text-sm text-warning-700">
        {t('baoCao.loiMotPhan', { so: tong.soLoi })}
      </div>
    )}

    <div className="grid grid-cols-12 gap-4">
      <Panel
        icon={<FileSearch size={16} />}
        tong="teal"
        tieuDe={t('baoCao.ocrTieuDe')}
        tieuDePhu={t('baoCao.loaiChungTu')}
        oSo={[
          { nhan: t('baoCao.mauOcr'), value: tong?.ocr.mauOcr ?? 0 },
          { nhan: t('baoCao.tongChungTu'), value: tong?.ocr.tongChungTu ?? 0 },
          { nhan: t('baoCao.daXacNhan'), value: tong?.ocr.daXacNhan ?? 0 },
          { nhan: t('baoCao.vaoKho'), value: tong?.ocr.vaoKho ?? 0 },
        ]}
        {...props}
      >
        <DanhSachThanh muc={tong?.ocr.theoLoai ?? []} rong={t('baoCao.chuaCoChungTu')} {...props} />
      </Panel>

      <Panel
        icon={<Database size={16} />}
        tong="violet"
        tieuDe={t('baoCao.khoTieuDe')}
        tieuDePhu={t('baoCao.tepTheoBo')}
        oSo={[
          { nhan: t('baoCao.boTriThuc'), value: tong?.kho.boTriThuc ?? 0 },
          { nhan: t('baoCao.tongTep'), value: tong?.kho.tongTep ?? 0 },
          { nhan: t('baoCao.phongBan'), value: tong?.kho.phongBan ?? 0 },
          { nhan: t('baoCao.tepPdf'), value: tong?.kho.tepPdf ?? 0 },
        ]}
        {...props}
      >
        <DanhSachThanh
          muc={tong?.kho.theoBo ?? []}
          max={tong?.kho.maxBo}
          rong={t('baoCao.chuaCoBo')}
          {...props}
        />
      </Panel>

      <Panel
        icon={<MessageSquare size={16} />}
        tong="sky"
        tieuDe={t('baoCao.botTieuDe')}
        tieuDePhu={t('baoCao.botTheoPhamVi')}
        oSo={[
          { nhan: t('baoCao.tongBot'), value: tong?.chatbot.tong ?? 0 },
          { nhan: t('baoCao.botHoatDong'), value: tong?.chatbot.hoatDong ?? 0 },
          { nhan: t('baoCao.coKichBan'), value: tong?.chatbot.coKichBan ?? 0 },
          { nhan: t('baoCao.traLoiDuoc'), value: tong?.chatbot.traLoiDuoc ?? 0 },
        ]}
        {...props}
      >
        <DanhSachThanh muc={tong?.chatbot.theoPhamVi ?? []} rong={t('baoCao.chuaCoBot')} {...props} />
      </Panel>

      <Panel
        icon={<Settings size={16} />}
        tong="warning"
        tieuDe={t('baoCao.heThongTieuDe')}
        tieuDePhu={t('baoCao.nguoiDungTheoPhongBan')}
        oSo={[
          { nhan: t('baoCao.nguoiDung'), value: tong?.heThong.nguoiDung ?? 0 },
          { nhan: t('baoCao.nguoiDungHoatDong'), value: tong?.heThong.hoatDong ?? 0 },
          { nhan: t('baoCao.vaiTro'), value: tong?.heThong.vaiTro ?? 0 },
          { nhan: t('baoCao.mauOcr'), value: tong?.heThong.mauOcr ?? 0 },
        ]}
        {...props}
      >
        <DanhSachThanh
          muc={tong?.heThong.theoPhongBan ?? []}
          rong={t('baoCao.chuaCoNguoiDung')}
          {...props}
        />
      </Panel>
    </div>
    </div>
  );

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[{ label: t('nav.nhom.tongQuan') }, { label: t('nav.muc.baoCaoThongKe') }]}
          titleIcon={<BarChart2 size={18} className="text-primary-600" />}
          title={t('baoCao.tieuDe')}
          subtitle={t('baoCao.phuDe')}
          actions={
            <>
              <span className="flex items-center gap-1.5 text-xs font-medium text-success-700">
                <span aria-hidden className="h-2 w-2 animate-pulse rounded-full bg-success-500" />
                {t('dashboard.aiDangHoatDong')}
              </span>
              <span className="hidden rounded-lg border border-default px-2.5 py-1 text-xs font-medium capitalize text-content-muted md:inline">
                {monthChip}
              </span>
              <button
                type="button"
                onClick={() => navigate('/thong-bao')}
                aria-label={t('nav.muc.thongBao')}
                title={t('nav.muc.thongBao')}
                className="rounded-lg p-1.5 text-content-muted transition-colors hover:bg-subtle hover:text-content-primary"
              >
                <Bell size={16} />
              </button>
              <span className="flex h-8 w-8 items-center justify-center rounded-full border border-default bg-subtle text-xs font-bold text-content-secondary">
                {avatarLetter}
              </span>
              <DropdownMenu
                menu={{
                  items: [
                    {
                      key: 'xlsx',
                      label: t('baoCao.xuatExcel'),
                      icon: <Download size={14} />,
                      onClick: () => void xuat('xlsx'),
                    },
                    {
                      key: 'pdf',
                      label: t('baoCao.xuatPdf'),
                      icon: <Download size={14} />,
                      onClick: () => void xuat('pdf'),
                    },
                  ],
                }}
              >
                <button
                  type="button"
                  disabled={!tong}
                  className="flex items-center gap-1.5 rounded-lg border border-default px-3 py-1.5 text-sm text-content-secondary transition-colors hover:bg-subtle hover:text-content-primary disabled:opacity-50"
                >
                  <Download size={14} />
                  {t('baoCao.xuatBaoCao')}
                </button>
              </DropdownMenu>
              <button
                type="button"
                onClick={() => {
                  void q.refetch();
                  if (coTokenAi) {
                    void qc.invalidateQueries({ queryKey: KHOA_MAN_TOKEN_AI.tatCa });
                    void qc.invalidateQueries({ queryKey: KHOA_TOKEN_AI.tatCa });
                    void qc.invalidateQueries({ queryKey: KHOA_BAN_TOKEN.tatCa });
                  }
                }}
                disabled={q.isFetching}
                className="flex items-center gap-1.5 rounded-lg border border-default px-3 py-1.5 text-sm text-content-secondary transition-colors hover:bg-subtle hover:text-content-primary disabled:opacity-50"
              >
                <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
                {t('chung.lamMoi')}
              </button>
            </>
          }
        />

        <TabsTruot
          xuongDong
          defaultActiveKey={moSan}
          onChange={(key) =>
            setThamSo(
              (cu) => {
                const moi = new URLSearchParams(cu);
                moi.set('the', key);
                // `muc` chỉ có nghĩa trong thẻ Token AI.
                if (key !== 'token-ai') moi.delete(THAM_SO_MUC_TOKEN);
                return moi;
              },
              { replace: true },
            )
          }
          items={[
            { key: 'tong-quan', label: t('baoCao.theTongQuan'), children: tongQuan },
            ...(coTokenAi && tenantId
              ? [{ key: 'token-ai', label: t('baoCao.theTokenAi'), children: <TabTokenAi tenantId={tenantId} /> }]
              : []),
          ]}
        />
      </div>
    </div>
  );
}
